import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import RmaRequestState from "@store/Store/Data/RmaRequest/RmaRequestState";
import { RmaAttachedFileModel, RmaLineModel, RmaModel } from "@store/Types/ApiModels";
import { Draft } from "immer";
import keyBy from "lodash/keyBy";
import merge from "lodash/merge";
import values from "lodash/values";

const initialState: RmaRequestState = {
    rmaNotes: "",
    rmaLineModels: [],
    canSendReturnRequest: false,
    rmaAttachedFiles: [],
};

const reducer = {
    "Data/RmaRequest/ChangeProductQtyOrdered": (
        draft: Draft<RmaRequestState>,
        action: { LineId: string; ReturnedQuantity: number },
    ) => {
        const rmaLine = draft.rmaLineModels.find(o => o.LineId === action.LineId);
        if (rmaLine) {
            rmaLine.ReturnedQuantity = action.ReturnedQuantity;
        }
    },
    "Data/RmaRequest/ChangeProductInvoiceNumber": (
        draft: Draft<RmaRequestState>,
        action: { LineId: string; InvoiceNumber?: string },
    ) => {
        const rmaLine = draft.rmaLineModels.find(o => o.LineId === action.LineId);
        if (rmaLine) {
            rmaLine.InvoiceNumber = action.InvoiceNumber;
        }
    },
    "Data/RmaRequest/ChangeRmaReasons": (
        draft: Draft<RmaRequestState>,
        action: { LineId: string; ReturnReason: string },
    ) => {
        const rmaLine = draft.rmaLineModels.find(o => o.LineId === action.LineId);
        if (rmaLine) {
            rmaLine.ReturnReason = action.ReturnReason;
        }
    },
    "Data/RmaRequest/ChangeSerialNumber": (
        draft: Draft<RmaRequestState>,
        action: { LineId: string; SerialNumber: string },
    ) => {
        const rmaLine = draft.rmaLineModels.find(o => o.LineId === action.LineId);
        if (rmaLine) {
            rmaLine.SerialNumber = action.SerialNumber;
        }
    },
    "Data/RmaRequest/ChangeRmaNotes": (draft: Draft<RmaRequestState>, action: any) => {
        draft.rmaNotes = action.parameter;
    },
    "Data/RmaRequest/ClearRmaNotes": (draft: Draft<RmaRequestState>) => {
        draft.rmaNotes = "";
    },
    "Data/RmaRequest/ClearRmaRequest": (draft: Draft<RmaRequestState>) => {
        draft.rmaLineModels = [];
        draft.rmaAttachedFiles = [];
    },
    "Data/RmaRequest/RemoveRmaLineProduct": (draft: Draft<RmaRequestState>, action: { LineId: string }) => {
        draft.rmaLineModels = draft.rmaLineModels.filter(o => !(o.LineId === action.LineId));
    },
    "Data/RmaRequest/SetRmaLine": (
        draft: Draft<RmaRequestState>,
        action: {
            LineId: string;
            ProductErpNumber: string;
            ProductDescription?: string;
            ReturnReason?: string;
            ReturnedQuantity: number;
        },
    ) => {
        draft.rmaLineModels = [
            ...draft.rmaLineModels,
            {
                LineId: action.LineId,
                ProductErpNumber: action.ProductErpNumber,
                ProductDescription: action.ProductDescription,
                ReturnReason: action.ReturnReason,
                ReturnedQuantity: action.ReturnedQuantity,
                InvoiceNumber: undefined,
                SerialNumber: undefined,
            },
        ];
    },
    "Data/RmaRequest/CompleteAddProduct": (draft: Draft<RmaRequestState>, action: { rmaLine: RmaLineModel }) => {
        draft.rmaLineModels.unshift(action.rmaLine);
    },
    // Request Reducer
    "Data/RmaRequest/BeginSendRmaRequest": (draft: Draft<RmaRequestState>) => {
        draft.resultMessage = undefined;
    },
    "Data/RmaRequest/CompleteSendRmaRequest": (draft: Draft<RmaRequestState>, action: { result?: RmaModel }) => {
        draft.resultMessage = action.result?.message;
    },
    "Data/RmaRequest/SetCanSendReturnRequest": (
        draft: Draft<RmaRequestState>,
        action: { canSendReturnRequest: boolean },
    ) => {
        draft.canSendReturnRequest = action.canSendReturnRequest;
    },
    "Data/RmaRequest/SetOrderLines": (draft: Draft<RmaRequestState>, action: { orderLines: RmaLineModel[] }) => {
        draft.rmaLineModels = action.orderLines;
    },
    "Data/RmaRequest/SetReturnNotes": (draft: Draft<RmaRequestState>, action: { returnNotes: string }) => {
        draft.rmaNotes = action.returnNotes;
    },
    "Data/RmaRequest/AddFileToRma": (draft: Draft<RmaRequestState>, action: { newFile: RmaAttachedFileModel }) => {
        const originalFilesByName = keyBy(draft.rmaAttachedFiles, o => o.Name);
        const newFilesByName = keyBy([action.newFile], o => o.Name);
        const mergedFiles = merge({}, originalFilesByName, newFilesByName);
        draft.rmaAttachedFiles = values(mergedFiles);
    },
    "Data/RmaRequest/RemoveFileFromRma": (draft: Draft<RmaRequestState>, action: { fileName: string }) => {
        draft.rmaAttachedFiles = draft.rmaAttachedFiles.filter(o => !(o.Name === action.fileName));
    },
};

export default createTypedReducerWithImmer(initialState, reducer);
